import api from "./api";
import { getPartnerHierarchy } from "features/offer/edit/client/form/api";
import { normalize } from "normalizr";
import { camelizeKeys } from "humps";
import * as actionTypes from "./actionTypes";
import { sendHttpRequest, sendHttpCommandAndWaitForEvent } from "actions/actionCreators/httpActions";
import { showError } from "actions/actionCreators/snackbarActions";
import * as eventTypes from "./eventTypes";
import schemas from "utils/schemas";

export function loadPartnersList(search) {
  //no redux cache
  return () => api.getPartnersList(search);
}

export function loadFacilityRevolvingTypes() {
  return async (dispatch, getState) => {
    const cachedFacilityRevolvingTypes = getState().facilities.facilityRevolvingTypes;
    if (cachedFacilityRevolvingTypes && (cachedFacilityRevolvingTypes.loaded || cachedFacilityRevolvingTypes.loading))
      return;
    try {
      dispatch({ type: actionTypes.LOAD_FACILITY_REVOLVING_TYPES_STARTED });
      const response = await dispatch(sendHttpRequest(api.getFacilityRevolvingTypes()));
      const camelizedJson = camelizeKeys(response);
      const payload = Object.assign({}, normalize(camelizedJson, schemas.facilityRevolvingTypes));
      dispatch({
        entitiesCategory: "facilities",
        type: actionTypes.LOAD_FACILITY_REVOLVING_TYPES_SUCCEEDED,
        ...payload
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_FACILITY_REVOLVING_TYPES_FAILED, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadFacilityFlexibilityRules() {
  return async (dispatch, getState) => {
    const cachedFacilityFlexibilityRules = getState().facilities.facilityFlexibilityRules;
    if (
      cachedFacilityFlexibilityRules &&
      (cachedFacilityFlexibilityRules.loaded || cachedFacilityFlexibilityRules.loading)
    )
      return;
    try {
      dispatch({ type: actionTypes.LOAD_FACILITY_FLEXIBILITY_RULES_STARTED });
      const response = await dispatch(sendHttpRequest(api.getFacilityFlexibilityRules()));
      const camelizedJson = camelizeKeys(response);
      const payload = Object.assign({}, normalize(camelizedJson, schemas.facilityFlexibilityRules));

      dispatch({
        entitiesCategory: "facilities",
        type: actionTypes.LOAD_FACILITY_FLEXIBILITY_RULES_SUCCEEDED,
        ...payload
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_FACILITY_FLEXIBILITY_RULES_FAILED, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadFacilityCreditLineStatuses() {
  return async (dispatch, getState) => {
    const cachedFacilityCreditLineStatuses = getState().facilities.facilityCreditLineStatuses;
    if (
      cachedFacilityCreditLineStatuses &&
      (cachedFacilityCreditLineStatuses.loaded || cachedFacilityCreditLineStatuses.loading)
    )
      return;
    try {
      dispatch({ type: actionTypes.LOAD_FACILITY_CREDIT_LINE_STATUSES_STARTED });
      const response = await dispatch(sendHttpRequest(api.getFacilityCreditLineStatuses()));
      const camelizedJson = camelizeKeys(response);
      const payload = Object.assign({}, normalize(camelizedJson, schemas.facilityCreditLineStatuses));

      dispatch({
        entitiesCategory: "facilities",
        type: actionTypes.LOAD_FACILITY_CREDIT_LINE_STATUSES_SUCCEEDED,
        ...payload
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_FACILITY_CREDIT_LINE_STATUSES_FAILED, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadFacilityPurposesForApplication() {
  return async (dispatch, getState) => {
    const cachedFacilityPurposesForApplication = getState().facilities.facilityPurposesForApplication;
    if (
      cachedFacilityPurposesForApplication &&
      (cachedFacilityPurposesForApplication.loaded || cachedFacilityPurposesForApplication.loading)
    )
      return;
    try {
      dispatch({ type: actionTypes.LOAD_FACILITY_PURPOSES_FOR_APPLICATION_STARTED });
      const response = await dispatch(sendHttpRequest(api.getFacilityPurposesForApplication()));
      const camelizedJson = camelizeKeys(response);
      const payload = Object.assign({}, normalize(camelizedJson, schemas.facilityPurposesForApplication));

      dispatch({
        entitiesCategory: "facilities",
        type: actionTypes.LOAD_FACILITY_PURPOSES_FOR_APPLICATION_SUCCEEDED,
        ...payload
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_FACILITY_PURPOSES_FOR_APPLICATION_FAILED, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadPartnerGroups() {
  return async (dispatch, getState) => {
    const facilityPartnerGroups = getState().facilities.facilityPartnerGroups;
    const shouldCallApi = !facilityPartnerGroups || (!facilityPartnerGroups.loaded && !facilityPartnerGroups.loading);
    if (!shouldCallApi) return;

    try {
      dispatch({ type: actionTypes.LOAD_PARTNER_GROUPS_STARTED });

      const response = await dispatch(sendHttpRequest(api.getPartnerGroups()));
      const camelizedJson = camelizeKeys(response);
      const payload = Object.assign({}, normalize(camelizedJson, schemas.facilityPartnerGroups));

      dispatch({
        entitiesCategory: "facilities",
        type: actionTypes.LOAD_PARTNER_GROUPS_SUCCEEDED,
        ...payload
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_PARTNER_GROUPS_FAILED, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadPartnerHierarchy(partnerGroupId) {
  return async (dispatch, getState) => {
    let facilityPartnerGroups = getState().facilities.facilityPartnerGroups;
    const shouldCallApi = !facilityPartnerGroups || (!facilityPartnerGroups.loaded && !facilityPartnerGroups.loading);
    if (!shouldCallApi) return;

    try {
      dispatch({ type: actionTypes.LOAD_PARTNER_GROUPS_STARTED });

      const response = await dispatch(sendHttpRequest(getPartnerHierarchy(partnerGroupId)));
      const camelizedJson = camelizeKeys(response);
      const payload = Object.assign({}, normalize(camelizedJson, schemas.facilityPartnerGroups));

      dispatch({
        entitiesCategory: "facilities",
        type: actionTypes.LOAD_PARTNER_GROUPS_SUCCEEDED,
        ...payload
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_PARTNER_GROUPS_FAILED, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadTransferToERPPermissions(documentId, siteId) {
  return async (dispatch, getState) => {
    let transferToERPPermissions = getState().facilities.transferFacilityToERPPermissions[documentId];
    const shouldCallApi =
      !transferToERPPermissions || (!transferToERPPermissions.loaded && !transferToERPPermissions.loading);
    if (!shouldCallApi) return;

    try {
      dispatch({ type: actionTypes.LOAD_TRANSFER_FACILITY_TO_ERP_PERMISSIONS_STARTED, key: documentId });
      const response = await dispatch(sendHttpRequest(api.getTransferToERPPermissions(documentId, siteId)));
      dispatch({
        type: actionTypes.LOAD_TRANSFER_FACILITY_TO_ERP_PERMISSIONS_SUCCEEDED,
        key: documentId,
        payload: response
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_TRANSFER_FACILITY_TO_ERP_PERMISSIONS_FAILED, key: documentId, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function saveFacilityHeader(item, documentId, siteId, callback = () => {}) {
  return async dispatch => {
    dispatch({
      type: actionTypes.SAVE_FACILITY_HEADER_STARTED
    });
    dispatch(
      sendHttpCommandAndWaitForEvent(api.saveFacilityHeader(item, documentId, siteId), "SaveFacilityHeader", [
        eventTypes.FACILITY_HEADER_SAVED
      ])
    )
      .then(event => {
        if (event.ok) {
          dispatch({
            type: actionTypes.SAVE_FACILITY_HEADER_SUCCEEDED,
            payload: { item }
          });
          callback(event.eventBody.documentId, event.eventBody.siteId);
        } else {
          dispatch({
            type: actionTypes.SAVE_FACILITY_HEADER_FAILED,
            payload: { item: item, error: event }
          });
          callback();
        }
      })
      .catch(error => {
        dispatch(showError(error.message, error.correlationId));
        dispatch({
          type: actionTypes.SAVE_FACILITY_HEADER_FAILED,
          payload: { item: item, error }
        });
        callback();
      });
  };
}

export function transferFacilityToERP(documentId, siteId, callback = () => {}) {
  return async dispatch =>
    dispatch(
      sendHttpCommandAndWaitForEvent(api.transferFacilityToERP(documentId, siteId), "TransferFacilityToERP", [
        eventTypes.TRANSFER_FACILITY_TO_ERP_EXECUTED
      ])
    )
      .then(event => {
        if (event.ok) {
          callback(event.eventBody);
        } else {
          callback();
        }
      })
      .catch(error => {
        dispatch(showError(error.message, error.correlationId));
        callback();
      });
}

export function loadFacilityWarranties(documentId, siteId) {
  return async (dispatch, getState) => {
    const facilityWarranties = getState().facilities.facilityWarranties[documentId];
    let shouldCallApi = !facilityWarranties || (!facilityWarranties.loaded && !facilityWarranties.loading);
    if (!shouldCallApi) return;

    try {
      dispatch({ type: actionTypes.LOAD_FACILITY_WARRANTIES_STARTED });

      const response = await dispatch(sendHttpRequest(api.getFacilityWarranties(documentId, siteId)));
      const camelizedJson = camelizeKeys(response);
      const payload = Object.assign({}, normalize(camelizedJson, schemas.facilityWarranties));

      dispatch({
        entitiesCategory: "facilities",
        type: actionTypes.LOAD_FACILITY_WARRANTIES_SUCCEEDED,
        key: documentId,
        ...payload
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_FACILITY_WARRANTIES_FAILED, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}
